import { useLayoutEffect, useMemo, useRef } from "react"
import { useAppSelector } from "hooks/redux"
import { kpiSlice, DEFAULT_KPI_STATE, KPI_PREFERENCE_SUBTYPE_BASE, selectKPIPanel, type SelectedGroup } from "connected-components/dashboard/store/kpi-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"

// Pure slice reader — does NOT sync preferences (KPIWrapper calls useSyncKPIPreferences once).
// Pass initialTab only from the wrapper; the seed is idempotent, so no-arg calls from children are safe.
export function useKPIData(initialTab?: string) {
  const { dashboardId, panelKey, dispatch, modelId, core, trade } = useDashboardSliceData(KPI_PREFERENCE_SUBTYPE_BASE)

  const kpiState = useAppSelector((s) => selectKPIPanel(s, dashboardId, panelKey) ?? DEFAULT_KPI_STATE)

  // Layout effect (registered before the sync's usePreferences) so the seed wins even when a
  // cache-warm fallback resolves synchronously on mount and omits selectedTab.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Wait for a tab before latching, or a config arriving on a later render never seeds.
    if (initialTab == null) return
    seeded.current = true
    dispatch(kpiSlice.actions.initFromModuleConfig({ dashboardId, panelId: panelKey, selectedTab: initialTab }))
  }, [dashboardId, panelKey, dispatch, initialTab])

  const kpiUpdaters = useMemo(() => ({
    setSectionExpanded: ({ key, value }: { key: string, value: unknown }) =>
      dispatch(kpiSlice.actions.setSectionExpanded({ dashboardId, panelId: panelKey, key, value })),
    setSelectedGroup: (group: SelectedGroup | null) =>
      dispatch(kpiSlice.actions.setSelectedGroup({ dashboardId, panelId: panelKey, group })),
    setSelectedTab: (tab: string) =>
      dispatch(kpiSlice.actions.setSelectedTab({ dashboardId, panelId: panelKey, tab })),
  }), [dispatch, dashboardId, panelKey])

  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...kpiState,
    ...kpiUpdaters,
  }), [core, modelId, trade, kpiState, kpiUpdaters])
}
